/* KPI strip — MUI cards: colored icon tile + left accent bar + big value + hover lift.
   cards: [{label, value, icon?(node), sub?, accent?(color), unit?, onClick?}]. */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Typography} from '@mui/material'
import {alpha} from '@mui/material/styles'

export default function KpiStrip({cards = [], className}) {
  return (
    <Box
      className={className}
      sx={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 1.75, mb: 2.5}}
    >
      {cards.map((c, i) => {
        // alpha() needs a real color — coerce CSS vars / unknown values to the brand blue
        const raw = c.accent || '#2065D1'
        const accent = /^(#|rgb|hsl)/i.test(raw) ? raw : '#2065D1'
        return (
          <Box
            key={c.label || i}
            onClick={c.onClick}
            role={c.onClick ? 'button' : undefined}
            tabIndex={c.onClick ? 0 : undefined}
            sx={{
              position: 'relative',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: '14px',
              p: 2,
              pl: 2.25,
              overflow: 'hidden',
              cursor: c.onClick ? 'pointer' : 'default',
              boxShadow: '0 1px 2px rgba(15,23,42,.04)',
              transition: 'box-shadow .18s ease, transform .18s ease',
              '&::before': {content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: accent},
              '&:hover': c.onClick
                ? {boxShadow: '0 8px 22px rgba(15,23,42,.12)', transform: 'translateY(-2px)'}
                : {boxShadow: '0 4px 14px rgba(15,23,42,.07)'},
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              {c.icon ? (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '9px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                    '& svg': {fontSize: 18},
                  }}
                >
                  {c.icon}
                </Box>
              ) : null}
              <Typography
                sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary'}}
              >
                {c.label}
              </Typography>
            </Box>
            <Typography sx={{fontSize: 26, fontWeight: 800, lineHeight: 1.1, mt: 1, letterSpacing: '-.02em', color: 'text.primary'}}>
              {c.value}
              {c.unit ? (
                <Box component='span' sx={{fontSize: 13, fontWeight: 600, color: 'text.secondary', ml: 0.5}}>
                  {c.unit}
                </Box>
              ) : null}
            </Typography>
            {c.sub ? <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 0.5}}>{c.sub}</Typography> : null}
          </Box>
        )
      })}
    </Box>
  )
}

KpiStrip.propTypes = {
  cards: PropTypes.array,
  className: PropTypes.string,
}
